'use client'

import Link from 'next/link'
import { useState } from 'react'
import { usePathname } from 'next/navigation'

// Read the CSRF token written by the server when the buyer session
// was created. Cookie is intentionally JS-readable (double-submit).
function readCsrfCookie(): string | null {
  const m = document.cookie.match(/(?:^|; )__Host-buyer\.csrf=([^;]+)/)
  return m ? decodeURIComponent(m[1]) : null
}

export function MessageVendorBlock({
  listingId,
  listingTitle,
  signedIn,
}: {
  listingId: string
  listingTitle: string
  signedIn: boolean
}) {
  const [open, setOpen] = useState(false)
  const [body, setBody] = useState(`Hi, is "${listingTitle}" still available?`)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sentConversationId, setSentConversationId] = useState<string | null>(null)
  const pathname = usePathname()

  if (!signedIn) {
    return (
      <div className="mb-4">
        <Link
          href={`/auth/buyer-signin?next=${encodeURIComponent(pathname)}`}
          className="block w-full text-center px-4 py-3 bg-white text-navy rounded text-sm font-semibold hover:brightness-95"
        >
          💬 Sign in to message vendor
        </Link>
        <div className="text-[11px] text-text-3 text-center mt-1.5">
          Quick SMS sign-in with your VoltCharge phone number
        </div>
      </div>
    )
  }

  async function send() {
    if (!body.trim() || sending) return
    setSending(true)
    setError(null)
    const csrf = readCsrfCookie()
    if (!csrf) {
      setError('Your session expired. Please sign in again.')
      setSending(false)
      return
    }
    try {
      const startRes = await fetch('/api/v1/marketplace/conversations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
        body: JSON.stringify({ listingId }),
      })
      const startData = (await startRes.json().catch(() => null)) as
        | { conversation?: { id: string }; error?: string }
        | null
      if (!startRes.ok || !startData?.conversation?.id) {
        setError(startData?.error ?? 'Could not start conversation.')
        setSending(false)
        return
      }
      const conversationId = startData.conversation.id
      const msgRes = await fetch(
        `/api/v1/marketplace/conversations/${conversationId}/messages`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
          body: JSON.stringify({ body: body.trim() }),
        },
      )
      if (!msgRes.ok) {
        const data = (await msgRes.json().catch(() => null)) as { error?: string } | null
        setError(data?.error ?? 'Could not send message.')
        setSending(false)
        return
      }
      setSentConversationId(conversationId)
    } catch {
      setError('Network error. Try again.')
    } finally {
      setSending(false)
    }
  }

  if (sentConversationId) {
    return (
      <div className="mb-4 bg-green/10 border border-green/30 rounded p-4 text-center">
        <div className="text-sm text-green font-semibold mb-1">✓ Message sent</div>
        <div className="text-xs text-text-2 mb-3">
          The vendor will see it next time they sign in.
        </div>
        <Link
          href={`/inbox/${sentConversationId}`}
          className="inline-block px-4 py-1.5 text-xs font-semibold border border-cyan/40 text-cyan rounded hover:bg-navy-3"
        >
          View in inbox →
        </Link>
      </div>
    )
  }

  if (!open) {
    return (
      <div className="mb-4">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="block w-full text-center px-4 py-3 bg-white text-navy rounded text-sm font-semibold hover:brightness-95"
        >
          💬 Message vendor
        </button>
      </div>
    )
  }

  return (
    <div className="mb-4 bg-navy-2 border border-cyan/30 rounded p-4 space-y-3">
      <div className="text-[10px] text-text-3 font-mono uppercase tracking-widest">
        Message vendor
      </div>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={4}
        maxLength={4000}
        disabled={sending}
        autoFocus
        className="w-full bg-navy-3 border border-[rgba(255,255,255,0.07)] rounded px-3 py-2 text-sm placeholder:text-text-3 focus:border-cyan/40 outline-none resize-y"
      />
      {error && (
        <div className="text-xs text-red-400 bg-red-400/10 border border-red-400/20 rounded px-3 py-2">
          {error}
        </div>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={send}
          disabled={sending || !body.trim()}
          className="flex-1 px-3 py-2 bg-white text-navy text-sm font-semibold rounded hover:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {sending ? 'Sending…' : 'Send →'}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          disabled={sending}
          className="px-3 py-2 text-text-2 border border-[rgba(255,255,255,0.07)] rounded text-sm hover:text-text-1 hover:border-cyan/20"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
